// Associations List Component
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Star,
  Trash2,
  Pencil,
  Loader2,
  ArrowRight,
  Link as LinkIcon
} from 'lucide-react';
import { associationAPI, crmAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { AssociationDetailDialog } from './ActivityDialogs';
import { useConfirm } from '../../components/common/ConfirmDialog';

// Normalize legacy relationship values to current enum keys
const RELATIONSHIP_ALIASES = { 'veterinarian': 'vet', 'vet_practice': 'other' };
const normalizeRel = (val) => RELATIONSHIP_ALIASES[val] || val;

const relationshipTypes = [
  { value: 'shareholder_company', label: 'Shareholder Company' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'employee', label: 'Employee' },
  { value: 'agent', label: 'Agent' },
  { value: 'trainer', label: 'Trainer' },
  { value: 'vet', label: 'Veterinarian' },
  { value: 'boarding', label: 'Boarding' },
  { value: 'family_close', label: 'Family (Close)' },
  { value: 'family_extended', label: 'Family (Extended)' },
  { value: 'other', label: 'Other' },
];

const associationStatuses = [
  { value: 'current_confirmed', label: 'Current (Confirmed)' },
  { value: 'current_possible', label: 'Current (Possible)' },
  { value: 'past_confirmed', label: 'Past (Confirmed)' },
  { value: 'past_possible', label: 'Past (Possible)' },
];

const AssociationsList = ({ entityId, tenantId, canEditAccount = true }) => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();

  const { data: associations = [], isLoading: loading } = useQuery({
    queryKey: QK.associations(entityId),
    queryFn: () => associationAPI.list(entityId).then(r => r.data),
    enabled: !!entityId,
  });

  const { data: entities = [] } = useQuery({
    queryKey: QK.crmEntities(),
    queryFn: () => crmAPI.list().then(r => r.data),
    enabled: !!entityId,
  });

  const { data: usersData = [] } = useQuery({
    queryKey: QK.tenantUsers(),
    queryFn: async () => {
      const r = await crmAPI.getUsers();
      return r.data || [];
    },
    enabled: !!entityId,
  });
  const users    = usersData;
  const usersMap = Object.fromEntries(usersData.map(u => [u.id, u]));

  const invalidateAssociations = () => qc.invalidateQueries({ queryKey: QK.associations(entityId) });
  
  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [editingAssoc, setEditingAssoc] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    crm_entity_2_id: '',
    relationship: 'other',
    status: 'current_confirmed',
    notes: '',
    is_key_association: false,
    job_title: '',
  });

  // Filter state
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchEntity, setSearchEntity] = useState('');

  const getEntityName = (id) => {
    const entity = entities.find(e => e.id === id);
    return entity ? entity.system_name : 'Unknown Entity';
  };

  const handleNavigateToEntity = (otherId) => {
    // Navigate to the entity profile with no query params to force default tab (Overview)
    navigate(`/crm/accounts/${otherId}`);
  };

  const handleOpenDialog = (assoc = null) => {
    if (assoc) {
      setEditingAssoc(assoc);
      // Determine which ID is the "other" entity
      const otherId = assoc.crm_entity_1_id === entityId ? assoc.crm_entity_2_id : assoc.crm_entity_1_id;
      setFormData({
        crm_entity_2_id: otherId,
        relationship: normalizeRel(assoc.relationship),
        status: assoc.status,
        notes: assoc.notes || '',
        is_key_association: assoc.is_key_association,
        job_title: assoc.job_title || '',
      });
    } else {
      setEditingAssoc(null);
      setFormData({
        crm_entity_2_id: '',
        relationship: 'other',
        status: 'current_confirmed',
        notes: '',
        is_key_association: false,
        job_title: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.crm_entity_2_id) {
      toast.error('Please select an entity to associate with.');
      return;
    }

    setSaving(true);
    try {
      if (editingAssoc) {
        // Update
        await associationAPI.update(editingAssoc.id, formData);
        toast.success('Association updated');
      } else {
        // Create
        // Ensure we send correct 1 and 2
        const payload = {
          ...formData,
          crm_entity_1_id: entityId, // Current entity is always 1 in this context creation
        };
        await associationAPI.create(payload);
        toast.success('Association created');
      }
      setIsDialogOpen(false);
      invalidateAssociations();
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save association');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!await confirm({ title: 'Are you sure you want to delete this association?', destructive: true })) return;
    try {
      await associationAPI.delete(id);
      toast.success('Association deleted');
      invalidateAssociations();
    } catch (error) {
      toast.error('Failed to delete association');
    }
  };

  const handleRowClick = (assoc) => {
    setSelectedAssociation(assoc);
    setDetailDialogOpen(true);
  };

  // Group and Filter
  const filteredAssociations = associations.filter(a => {
    // Text search filter
    const matchesSearch = searchTerm === '' || 
      getEntityName(a.crm_entity_1_id === entityId ? a.crm_entity_2_id : a.crm_entity_1_id)
        .toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.relationship || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.job_title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (a.notes || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const statusPart = a.status.split('_')[1]; // 'confirmed' or 'possible'
    const matchesStatus = filterStatus === 'all' || statusPart === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Split into Current and Past
  const currentAssociations = filteredAssociations.filter(a => a.status.startsWith('current'));
  const pastAssociations = filteredAssociations.filter(a => a.status.startsWith('past'));

  // Helper to get status label (Confirmed/Possible)
  const getStatusLabel = (status) => {
    if (status.includes('confirmed')) return 'Confirmed';
    if (status.includes('possible')) return 'Possible';
    return status;
  };

  // Helper to get status badge variant
  const getStatusVariant = (status) => {
    return status.includes('confirmed') ? 'default' : 'secondary';
  };

  if (loading) return <div className="p-4"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-medium">All Associations</h3>
        {canEditAccount && (
          <Button onClick={() => handleOpenDialog()} variant="ghost" size="sm" className="h-6">
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        <div className="flex-1 max-w-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search associations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="w-[200px]">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger>
              <SelectValue placeholder="Filter by Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="possible">Possible</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {currentAssociations.length === 0 && pastAssociations.length === 0 ? (
        <p className="text-center text-sm text-muted-foreground py-8">No associations found.</p>
      ) : (
        <div className="space-y-6">
          {/* Current Associations Table */}
          {currentAssociations.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" />
                  Current Associations ({currentAssociations.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs w-[25%]">Entity</TableHead>
                      <TableHead className="text-xs w-[20%]">Relationship</TableHead>
                      <TableHead className="text-xs w-[15%]">Status</TableHead>
                      <TableHead className="text-xs w-[15%]">Job Title</TableHead>
                      <TableHead className="text-xs w-[25%]">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentAssociations.map(assoc => {
                      const otherId = assoc.crm_entity_1_id === entityId ? assoc.crm_entity_2_id : assoc.crm_entity_1_id;
                      return (
                        <TableRow 
                          key={assoc.id}
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleRowClick(assoc)}
                        >
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-2">
                              {assoc.is_key_association && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                              <span
                                className="text-primary hover:underline cursor-pointer"
                                onClick={async (e) => { e.stopPropagation(); handleNavigateToEntity(otherId); }}
                              >
                                {getEntityName(otherId)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <Badge variant="outline" className="text-xs">
                              {relationshipTypes.find(r => r.value === normalizeRel(assoc.relationship))?.label || assoc.relationship}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            <Badge variant={getStatusVariant(assoc.status)} className="text-xs">
                              {getStatusLabel(assoc.status)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{assoc.job_title || '-'}</TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate text-muted-foreground" title={assoc.notes}>
                            {assoc.notes || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Past Associations Table */}
          {pastAssociations.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <LinkIcon className="w-4 h-4" />
                  Past Associations ({pastAssociations.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs w-[25%]">Entity</TableHead>
                      <TableHead className="text-xs w-[20%]">Relationship</TableHead>
                      <TableHead className="text-xs w-[15%]">Status</TableHead>
                      <TableHead className="text-xs w-[15%]">Job Title</TableHead>
                      <TableHead className="text-xs w-[25%]">Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pastAssociations.map(assoc => {
                      const otherId = assoc.crm_entity_1_id === entityId ? assoc.crm_entity_2_id : assoc.crm_entity_1_id;
                      return (
                        <TableRow 
                          key={assoc.id}
                          className="cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => handleRowClick(assoc)}
                        >
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-2">
                              {assoc.is_key_association && <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />}
                              <span
                                className="text-primary hover:underline cursor-pointer"
                                onClick={async (e) => { e.stopPropagation(); handleNavigateToEntity(otherId); }}
                              >
                                {getEntityName(otherId)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <Badge variant="outline" className="text-xs">
                              {relationshipTypes.find(r => r.value === normalizeRel(assoc.relationship))?.label || assoc.relationship}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            <Badge variant={getStatusVariant(assoc.status)} className="text-xs">
                              {getStatusLabel(assoc.status)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">{assoc.job_title || '-'}</TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate text-muted-foreground" title={assoc.notes}>
                            {assoc.notes || '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{editingAssoc ? 'Edit Association' : 'Add New Association'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Associate With *</Label>
              {/* Searchable select for entities would be better, using simple select for now */}
              <Select 
                value={formData.crm_entity_2_id} 
                onValueChange={(v) => setFormData({...formData, crm_entity_2_id: v})}
                disabled={!!editingAssoc} // Disable changing entity on edit to simplify
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Entity" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {entities
                    .filter(e => e.id !== entityId) // Exclude self
                    .sort((a, b) => a.system_name.localeCompare(b.system_name))
                    .map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.system_name}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Relationship *</Label>
                <Select 
                  key={`rel-${editingAssoc?.id || 'new'}`}
                  value={formData.relationship || undefined}
                  onValueChange={(v) => setFormData({...formData, relationship: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    {relationshipTypes.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status *</Label>
                <Select 
                  value={formData.status} 
                  onValueChange={(v) => setFormData({...formData, status: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {associationStatuses.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Job Title / Role Description</Label>
              <Input 
                value={formData.job_title}
                onChange={(e) => setFormData({...formData, job_title: e.target.value})}
                placeholder="e.g. Head Trainer"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input 
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Details about this relationship..."
              />
            </div>

            <div className="flex items-center space-x-2 pt-2">
              <Checkbox 
                id="key_assoc"
                checked={formData.is_key_association}
                onCheckedChange={(c) => setFormData({...formData, is_key_association: c})}
              />
              <Label htmlFor="key_assoc" className="font-medium flex items-center gap-2">
                Key Association <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Association'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Association Detail Dialog */}
      <AssociationDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        association={selectedAssociation}
        entityId={entityId}
        entities={entities}
        usersMap={usersMap}
        onSuccess={() => {
          setDetailDialogOpen(false);
          invalidateAssociations();
        }}
        canEditAccount={true}
      />
    </div>
  );
};

export default AssociationsList;
